import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/currencyCtx";
import { fmt, formatPct } from "@/lib/currency";
import { Crown, TrendingUp, Coins, Hourglass, Award, Sparkles, Users } from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  RadialBarChart, RadialBar, PolarAngleAxis,
} from "recharts";

const CHART = ["#0A2A24", "#19433A", "#2C5C50", "#C5A059", "#8E6F32"];
const COMPOSITION_COLORS = {
  Revenue: "#0A2A24",
  Capital: "#19433A",
  Tenure: "#2C5C50",
  Leadership: "#C5A059",
  Founder: "#8E6F32",
};

const CATEGORY_META = [
  { key: "revenue",    label: "Revenue Contribution", icon: TrendingUp, totalKey: "revenue",          shareKey: "revenue_share",    myKey: "revenue_contribution", unit: "currency" },
  { key: "capital",    label: "Capital Contribution", icon: Coins,      totalKey: "capital",          shareKey: "capital_share",    myKey: "capital_contribution", unit: "currency" },
  { key: "tenure",     label: "Partner Tenure",       icon: Hourglass,  totalKey: "tenure_points",    shareKey: "tenure_share",     myKey: "tenure_points",        unit: "points" },
  { key: "leadership", label: "Leadership Points",    icon: Award,      totalKey: "leadership_points",shareKey: "leadership_share", myKey: "leadership_points",    unit: "points" },
  { key: "founder",    label: "Founders Points",      icon: Sparkles,   totalKey: "num_founders",     shareKey: "founder_share",    myKey: "founder_flag",         unit: "flag" },
];

export default function PartnerDashboard() {
  const { currency, rate } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/partner/me");
      setData(data);
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="eyebrow animate-pulse">Loading…</div>;

  if (!data?.linked) {
    return (
      <div className="anim-fade-up max-w-xl">
        <div className="eyebrow mb-2">Welcome</div>
        <h1 className="display-serif text-4xl text-atf-brand">Your partner profile is being set up.</h1>
        <div className="gold-rule-short mt-4 mb-4" />
        <p className="text-atf-text2 mt-4">
          The administrator hasn’t linked your sign-in to a partner record yet. Please ask them to verify your email is on file.
        </p>
      </div>
    );
  }

  const p = data.partner;
  const latest = data.latest;
  const firm = data.firm || { total_partners: 0, founders: 0, by_class: {}, by_leadership: {} };

  if (!latest) {
    return (
      <div className="anim-fade-up max-w-2xl">
        <div className="eyebrow mb-2">Welcome, {p.name}</div>
        <h1 className="display-serif text-5xl text-atf-brand">Awaiting first publication.</h1>
        <div className="gold-rule-short mt-4 mb-6" />
        <p className="text-atf-text2 mt-2">No distribution has been published yet. Your dashboard will appear here once it is.</p>
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-0 luxe-card">
          <Stat label="Class" value={p.position} />
          <Stat label="Tenure" value={`${p.tenure_years} yrs`} />
          <Stat label="Founder" value={p.is_founder ? "Yes" : "No"} />
          <Stat label="Region" value={p.region || "—"} />
        </div>
        <div className="mt-6">
          <FirmCompositionPanel firm={firm} />
        </div>
      </div>
    );
  }

  const r = latest.partner_row;
  const totals = latest.totals || {};
  const subPools = latest.sub_pools || {};
  const sliceData = [
    { name: "Revenue", value: r.revenue_share },
    { name: "Capital", value: r.capital_share },
    { name: "Tenure", value: r.tenure_share },
    { name: "Leadership", value: r.leadership_share },
    { name: "Founder", value: r.founder_share },
  ].filter((s) => s.value > 0);

  const histData = (data.history || [])
    .map((h) => ({ year: String(h.year), amount: h.partner_row.total_share }))
    .reverse();

  // Stacked horizontal "where your share came from" data — single row
  const stackData = [{
    name: "share",
    Revenue: r.revenue_share,
    Capital: r.capital_share,
    Tenure: r.tenure_share,
    Leadership: r.leadership_share,
    Founder: r.founder_share,
  }];

  return (
    <div className="anim-stagger space-y-10">
      {/* ============ HERO ============ */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Personal share */}
        <div className="lg:col-span-8 luxe-card gilded p-10 paper-grain">
          <div className="flex items-center gap-3 mb-6">
            <div className="eyebrow">Your share · FY {latest.year}</div>
            {p.is_founder && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 gold-foil text-[10px] uppercase tracking-wider font-semibold">
                <Crown size={10} /> Founding Equity
              </span>
            )}
          </div>
          <div className="font-serif text-7xl sm:text-8xl tracking-tighter text-atf-brand leading-none mb-3" data-testid="profit-share-value">
            {fmt(r.total_share, currency, rate)}
          </div>
          <div className="gold-rule-short mt-5 mb-5" />
          <div className="text-atf-text2 text-base" data-testid="profit-share-pct">
            <span className="tabular text-atf-gold3 font-semibold">{formatPct(r.pct_of_pool)}</span>{" "}
            of the firm’s distributable pool of{" "}
            <span className="tabular text-atf-text">{fmt(latest.distributable_pool, currency, rate)}</span>.
          </div>
          <div className="rule my-8" />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
            <Mini label="Class" value={p.position} />
            <Mini label="Tenure" value={`${r.tenure_years} yrs`} sub={`${r.tenure_points} pts`} />
            <Mini label="Leadership" value={p.leadership_role || "—"} sub={`${r.leadership_points} pts`} />
            <Mini label="Revenue brought in" value={fmt(r.revenue_contribution, currency, rate)} />
            <Mini label="Capital committed" value={fmt(r.capital_contribution, currency, rate)} />
            <Mini label="Region" value={p.region || "—"} />
          </div>
        </div>

        {/* Firm summary */}
        <div className="lg:col-span-4 luxe-card-dark p-8">
          <div className="eyebrow !text-atf-gold mb-2">Firm-wide · FY {latest.year}</div>
          <h2 className="font-serif text-3xl mb-6 text-white">{latest.title}</h2>
          <FirmLine label="Net Profit" value={fmt(latest.net_profit, currency, rate)} />
          <FirmLine label="Growth Reserve" value={fmt(latest.net_profit - latest.distributable_pool, currency, rate)} sub="reserved for the firm" />
          <FirmLine label="Distributable Pool" value={fmt(latest.distributable_pool, currency, rate)} sub="shared by all partners" highlight />
          <div className="my-6 h-px bg-white/10" />
          <div className="eyebrow !text-atf-gold mb-3">Phase weights applied</div>
          <div className="grid grid-cols-5 gap-1 font-mono text-[11px]">
            {Object.entries(latest.weights).map(([k, v]) => (
              <div key={k} className="text-center py-1.5 bg-white/10 text-white" title={k}>{v}%</div>
            ))}
          </div>
          <div className="grid grid-cols-5 gap-1 mt-1 text-[10px] uppercase tracking-wider text-white/55">
            {Object.keys(latest.weights).map((k) => (<div key={k} className="text-center">{k.slice(0, 3)}</div>))}
          </div>
        </div>
      </section>

      {/* ============ FIRM COMPOSITION ============ */}
      <FirmCompositionPanel firm={firm} />

      {/* ============ CATEGORY BREAKDOWN ============ */}
      <section>
        <SectionHeader eyebrow="Your contribution, line by line" title="The five drivers of your share" />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 mt-6">
          {CATEGORY_META.map((c) => (
            <CategoryCard
              key={c.key}
              meta={c}
              partnerRow={r}
              totals={totals}
              subPools={subPools}
              weight={latest.weights[c.key]}
              currency={currency}
              rate={rate}
              isFounder={p.is_founder}
            />
          ))}
        </div>
      </section>

      {/* ============ CHARTS ============ */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="luxe-card p-6">
          <SectionHeader eyebrow="Composition" title="Where your share came from" small />
          <div className="mt-4" style={{ height: 260 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={sliceData} dataKey="value" innerRadius={70} outerRadius={110} paddingAngle={3} stroke="#FFFCF5" strokeWidth={2}>
                  {sliceData.map((s, i) => <Cell key={i} fill={COMPOSITION_COLORS[s.name]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmt(v, currency, rate)} contentStyle={tipStyle} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4">
            {sliceData.map((s) => (
              <div key={s.name} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2"><span className="w-3 h-3" style={{ background: COMPOSITION_COLORS[s.name] }} />{s.name}</span>
                <span className="tabular text-atf-text">{fmt(s.value, currency, rate)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="luxe-card p-6">
          <SectionHeader eyebrow="Composition (stacked)" title="One year, five rivers" small />
          <div className="mt-4" style={{ height: 80 }}>
            <ResponsiveContainer>
              <BarChart data={stackData} layout="vertical" margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" hide />
                <Tooltip formatter={(v) => fmt(v, currency, rate)} contentStyle={tipStyle} />
                {["Revenue", "Capital", "Tenure", "Leadership", "Founder"].map((k) => (
                  <Bar key={k} dataKey={k} stackId="a" fill={COMPOSITION_COLORS[k]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="rule my-5" />
          <SectionHeader eyebrow="History" title="Your distribution over time" small />
          {histData.length === 0 ? (
            <div className="text-sm text-atf-text2 mt-3">No history yet.</div>
          ) : (
            <div className="mt-4" style={{ height: 180 }}>
              <ResponsiveContainer>
                <BarChart data={histData}>
                  <CartesianGrid stroke="#E6D9B5" vertical={false} />
                  <XAxis dataKey="year" stroke="#6B604D" fontSize={11} />
                  <YAxis stroke="#6B604D" fontSize={11} tickFormatter={(v) => fmt(v, currency, rate)} width={70} />
                  <Tooltip formatter={(v) => fmt(v, currency, rate)} contentStyle={tipStyle} />
                  <Bar dataKey="amount" fill="#C5A059" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </section>

      {/* ============ HISTORY TABLE ============ */}
      {data.history && data.history.length > 1 && (
        <section className="luxe-card">
          <div className="px-6 py-4 border-b border-atf-border-soft">
            <div className="eyebrow">Published distributions</div>
          </div>
          <table className="w-full text-sm" data-testid="partner-history-table">
            <thead><tr className="border-b border-atf-border-soft">
              <Th>Year</Th><Th>Title</Th><Th align="right">Your share</Th><Th align="right">% of pool</Th><Th align="right">Firm pool</Th>
            </tr></thead>
            <tbody>
              {data.history.map((h) => (
                <tr key={h.distribution_id} className="border-b border-atf-border-soft last:border-b-0">
                  <Td><span className="font-serif text-xl text-atf-brand">{h.year}</span></Td>
                  <Td>{h.title}</Td>
                  <Td align="right" mono><strong className="text-atf-brand">{fmt(h.partner_row.total_share, currency, rate)}</strong></Td>
                  <Td align="right" mono>{formatPct(h.partner_row.pct_of_pool)}</Td>
                  <Td align="right" mono>{fmt(h.distributable_pool, currency, rate)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

/* ===================================================================== */
function FirmCompositionPanel({ firm }) {
  const classData = Object.entries(firm.by_class || {}).map(([name, value]) => ({ name, value }));
  const leadershipData = Object.entries(firm.by_leadership || {}).map(([name, value]) => ({ name, value }));

  return (
    <section>
      <SectionHeader eyebrow="The Partnership" title="Who you stand alongside" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="luxe-card p-7 flex flex-col justify-between">
          <div>
            <div className="eyebrow">Total Partners</div>
            <div className="font-serif text-7xl text-atf-brand leading-none mt-3" data-testid="total-partners">{firm.total_partners}</div>
            <div className="gold-rule-short mt-5 mb-5" />
            <div className="text-sm text-atf-text2">Active equity-bearing partners across the firm.</div>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-4 pt-6 border-t border-atf-border-soft">
            <div>
              <div className="eyebrow">Founders</div>
              <div className="font-serif text-3xl text-atf-gold3 mt-1.5" data-testid="founders-count">{firm.founders}</div>
            </div>
            <div>
              <div className="eyebrow">In Leadership</div>
              <div className="font-serif text-3xl text-atf-brand mt-1.5">
                {Object.values(firm.by_leadership || {}).reduce((a, b) => a + b, 0)}
              </div>
            </div>
          </div>
        </div>

        <div className="luxe-card p-6">
          <SectionHeader eyebrow="By Class" title="Partnership tiers" small />
          {classData.length === 0 ? (
            <div className="text-sm text-atf-text2 mt-3">No partners yet.</div>
          ) : (
            <>
              <div className="mt-4" style={{ height: 200 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={classData} dataKey="value" innerRadius={55} outerRadius={85} paddingAngle={3} stroke="#FFFCF5" strokeWidth={2}>
                      {classData.map((_, i) => <Cell key={i} fill={CHART[i % CHART.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={tipStyle} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-3 space-y-2" data-testid="class-breakdown">
                {classData.map((c, i) => (
                  <div key={c.name} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-2"><span className="w-2.5 h-2.5" style={{ background: CHART[i % CHART.length] }} />{c.name}</span>
                    <span className="tabular">{c.value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="luxe-card p-6">
          <SectionHeader eyebrow="Leadership Roles" title="Who steers the firm" small />
          {leadershipData.length === 0 ? (
            <div className="text-sm text-atf-text2 mt-3">No leadership roles assigned yet.</div>
          ) : (
            <div className="mt-4 space-y-3" data-testid="leadership-breakdown">
              {leadershipData.map((l) => {
                const total = leadershipData.reduce((a, b) => a + b.value, 0);
                const pct = (l.value / total) * 100;
                return (
                  <div key={l.name}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="text-atf-text">{l.name}</span>
                      <span className="tabular text-atf-text2">{l.value}</span>
                    </div>
                    <div className="gauge"><span style={{ width: `${pct}%` }} /></div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/* ===================================================================== */
function CategoryCard({ meta, partnerRow, totals, subPools, weight, currency, rate, isFounder }) {
  const Icon = meta.icon;
  const subPool = subPools[meta.key] || 0;
  const myShare = partnerRow[meta.shareKey] || 0;

  let myValue, totalValue, valueLabel, totalLabel, pct;
  if (meta.key === "revenue") {
    myValue = partnerRow.revenue_contribution;
    totalValue = totals.revenue || 0;
    valueLabel = fmt(myValue, currency, rate);
    totalLabel = fmt(totalValue, currency, rate);
    pct = totalValue > 0 ? (myValue / totalValue) * 100 : 0;
  } else if (meta.key === "capital") {
    myValue = partnerRow.capital_contribution;
    totalValue = totals.capital || 0;
    valueLabel = fmt(myValue, currency, rate);
    totalLabel = fmt(totalValue, currency, rate);
    pct = totalValue > 0 ? (myValue / totalValue) * 100 : 0;
  } else if (meta.key === "tenure") {
    myValue = partnerRow.tenure_points;
    totalValue = totals.tenure_points || 0;
    valueLabel = `${myValue} pts`;
    totalLabel = `${totalValue} pts`;
    pct = totalValue > 0 ? (myValue / totalValue) * 100 : 0;
  } else if (meta.key === "leadership") {
    myValue = partnerRow.leadership_points;
    totalValue = totals.leadership_points || 0;
    valueLabel = `${myValue} pts`;
    totalLabel = `${totalValue} pts`;
    pct = totalValue > 0 ? (myValue / totalValue) * 100 : 0;
  } else {
    // founder
    myValue = isFounder ? 1 : 0;
    totalValue = totals.num_founders || 0;
    valueLabel = isFounder ? "Yes" : "No";
    totalLabel = `${totalValue} founder${totalValue === 1 ? "" : "s"}`;
    pct = totalValue > 0 && isFounder ? (1 / totalValue) * 100 : 0;
  }

  return (
    <div className="luxe-card p-5 flex flex-col" data-testid={`category-card-${meta.key}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="w-8 h-8 flex items-center justify-center bg-atf-brand text-atf-gold">
          <Icon size={15} />
        </div>
        <div className="eyebrow !text-atf-gold3">{weight}% weight</div>
      </div>
      <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-atf-text2 mb-1">{meta.label}</div>

      <div className="mt-2">
        <div className="eyebrow !text-[10px]">My contribution</div>
        <div className="font-serif text-2xl text-atf-brand tabular mt-0.5" data-testid={`my-value-${meta.key}`}>{valueLabel}</div>
      </div>

      <div className="mt-3">
        <div className="eyebrow !text-[10px]">Firm total</div>
        <div className="font-mono text-sm text-atf-text2 tabular" data-testid={`total-value-${meta.key}`}>{totalLabel}</div>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between text-[11px] mb-1.5">
          <span className="eyebrow !text-[10px]">Your share of category</span>
          <span className="tabular font-semibold text-atf-gold3" data-testid={`my-pct-${meta.key}`}>{formatPct(pct, 1)}</span>
        </div>
        <div className="gauge"><span style={{ width: `${Math.min(pct, 100)}%` }} /></div>
      </div>

      <div className="mt-auto pt-4 border-t border-atf-border-soft">
        <div className="eyebrow !text-[10px]">Earned from this driver</div>
        <div className="font-serif text-2xl text-atf-gold3 tabular mt-0.5" data-testid={`earned-${meta.key}`}>{fmt(myShare, currency, rate)}</div>
        <div className="font-mono text-[10px] text-atf-text3 mt-0.5">sub-pool {fmt(subPool, currency, rate)}</div>
      </div>
    </div>
  );
}

/* ===================================================================== */
const tipStyle = {
  background: "#FFFCF5",
  border: "1px solid #D9C99E",
  borderRadius: 0,
  fontFamily: "IBM Plex Mono",
  fontSize: 12,
  color: "#1B1611",
};

const SectionHeader = ({ eyebrow, title, small }) => (
  <div>
    <div className="eyebrow">{eyebrow}</div>
    <h2 className={`font-serif ${small ? "text-2xl" : "text-3xl sm:text-4xl"} text-atf-brand mt-1`}>{title}</h2>
    <div className="gold-rule-short mt-3" />
  </div>
);

const Mini = ({ label, value, sub }) => (
  <div>
    <div className="eyebrow">{label}</div>
    <div className="font-serif text-xl text-atf-brand mt-1.5 leading-tight">{value}</div>
    {sub && <div className="text-[11px] font-mono text-atf-text2 mt-0.5">{sub}</div>}
  </div>
);
const FirmLine = ({ label, value, sub, highlight }) => (
  <div className="py-3 border-b border-white/10 last:border-b-0">
    <div className="flex items-baseline justify-between gap-4">
      <span className="eyebrow !text-atf-gold/70">{label}</span>
      <span className={`font-serif text-xl ${highlight ? "text-atf-gold" : "text-white"} tabular`}>{value}</span>
    </div>
    {sub && <div className="text-[10px] text-white/40 mt-0.5">{sub}</div>}
  </div>
);
const Stat = ({ label, value }) => (
  <div className="p-5 border-r border-atf-border-soft last:border-r-0">
    <div className="eyebrow">{label}</div>
    <div className="font-serif text-xl text-atf-brand mt-2">{value}</div>
  </div>
);
const Th = ({ children, align = "left" }) => <th className={`px-4 py-3 eyebrow text-${align}`}>{children}</th>;
const Td = ({ children, align = "left", mono }) => <td className={`px-4 py-3 text-${align} ${mono ? "tabular" : ""}`}>{children}</td>;
